// session ของผู้ใช้ — เก็บเฉพาะ token + ข้อมูลแสดงผล ไม่เก็บรหัสผ่าน
//
// ชื่อ key ห้ามเปลี่ยน: iframe ของ Inbox Center อ่าน token จาก storage ตัวเดียวกัน
// (same-origin ผ่าน proxy) และ relay ทุกตัวส่งต่อ token นี้ให้ระบบปลายทาง
// token มาจาก Wazzup หรือ IAMService ก็ได้ — รูปร่างเหมือนกัน (HS256 JWT)
//
// **เก็บใน localStorage ไม่ใช่ sessionStorage** — sessionStorage แยกกันคนละแท็บ
// คนที่ login ไว้แล้วแต่กดลิงก์จากระบบอื่นมาเปิดแท็บใหม่ จะเจอหน้า login ทั้งที่
// เพิ่งใช้งานอยู่ในอีกแท็บ · Inbox Center เก็บแบบนี้อยู่ก่อนแล้ว (wz_session)
//
// อายุของ session ยังคุมด้วย expiration ของ token เหมือนเดิม — getWazzupSession()
// ลบทิ้งเมื่อหมดอายุ ไม่ได้อยู่ยาวกว่าที่ token อนุญาต
const KEY = "wazzup_session";

export type WazzupSession = {
  access_token: string;
  expiration?: string;
  nickName?: string;
  empEngName?: string;
  profileURL?: string;
  /** ข้อมูลแสดงบนเมนูโปรไฟล์ — SSO ไม่ได้มาใน token ต้องดึงจาก /wazzup/profile ทีหลัง */
  empCode?: string;
  empThaiName?: string;
  positionName?: string;
  departmentName?: string;
  /** login มาทางไหน — ใช้ตัดสินใจว่าตอน logout ต้องไปล้าง cookie ของ IAM ด้วยไหม */
  via?: "password" | "sso";
  /** role จาก IAM (SSO ส่งมาใน claim ของ token แล้วทับด้วย userRole จากโปรไฟล์) */
  roles?: string[];
  /**
   * ดึงโปรไฟล์เต็มมาแล้วหรือยัง — เช็คจากค่าอื่นไม่ได้ เพราะ "ไม่มีสิทธิเลย" กับ
   * "ยังไม่ได้ถาม" หน้าตาเหมือนกัน แล้วจะกลายเป็นยิงซ้ำทุกครั้งที่เปิดหน้า
   */
  profileFetched?: boolean;
};

export function getWazzupSession(): WazzupSession | null {
  try {
    // ย้ายของเดิมที่ค้างใน sessionStorage มาให้ครั้งเดียว — คนที่เปิดแอปค้างไว้
    // ตอน deploy จะได้ไม่ถูกเด้งออกเพราะเราเปลี่ยนที่เก็บ
    const stale = sessionStorage.getItem(KEY);
    if (stale && !localStorage.getItem(KEY)) localStorage.setItem(KEY, stale);
    if (stale) sessionStorage.removeItem(KEY);

    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as WazzupSession;
    if (!s?.access_token) return null;
    // token หมดอายุแล้วให้ลบทิ้ง ไม่ต้องรอให้ปลายทางตอบ 401
    if (s.expiration && new Date(s.expiration) < new Date()) {
      localStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function setSession(session: WazzupSession): void {
  localStorage.setItem(KEY, JSON.stringify(session));
}

export function clearWazzupSession(): void {
  localStorage.removeItem(KEY);
  sessionStorage.removeItem(KEY); // เผื่อของเก่าที่ยังค้าง
}

/** ตัวย่อบนวงกลมโปรไฟล์ตอนไม่มีรูป */
export function sessionInitials(s: WazzupSession | null): string {
  if (!s) return "AN";
  return (s.nickName || s.empEngName || "?").trim().slice(0, 2).toUpperCase();
}
